import { JIRA_INTEGRATION_STATUS, JIRA_INTEGRATION_STATUS_LABELS } from './jiraIntegration.constants';
import {
  JIRA_CONNECTION_HEALTH_ISSUE,
  JIRA_CONNECTION_HEALTH_LABELS,
  JIRA_CONNECTION_HEALTH_LINK_ACTION,
  JIRA_CONNECTION_METRIC_LABELS,
  JIRA_SYSTEM_CHECK_STATUS,
  JIRA_TEST_CONNECTION_LABELS
} from './jiraIntegrationDetail.constants';

// TODO: Revisit transforms once the Jira integration API returns final response shapes.
export function transformJiraIntegrationSummary(jiraIntegration) {
  if (!jiraIntegration) {
    return null;
  }

  return {
    status: jiraIntegration.status,
    instanceUrl: jiraIntegration.instanceUrl,
    spacesCount: jiraIntegration.spacesCount,
    lastUpdated: jiraIntegration.lastUpdated,
    oauthTokenActive: jiraIntegration.oauthTokenActive ?? false,
    apiConnectivityActive: jiraIntegration.apiConnectivityActive ?? false,
    jiraInstallationId: jiraIntegration.jiraInstallationId ?? null,
    oauthInstallStepComplete: jiraIntegration.oauthInstallStepComplete ?? false,
    jiraAppInstalledComplete: jiraIntegration.jiraAppInstalledComplete ?? false
  };
}

export function transformJiraIntegrationConnectionHealth(jiraIntegration) {
  if (!jiraIntegration) {
    return null;
  }

  const connectionHealth = transformJiraConnectionHealth(jiraIntegration.connectionHealth);
  const status = jiraIntegration.status;
  // Backend used to map disabled installs to PENDING; treat that label as Disconnected in the UI.
  const connectionStatusLabel =
    status === JIRA_INTEGRATION_STATUS.NOT_CONNECTED || status === JIRA_INTEGRATION_STATUS.PENDING
      ? JIRA_INTEGRATION_STATUS_LABELS[JIRA_INTEGRATION_STATUS.NOT_CONNECTED]
      : connectionHealth?.connectionStatusLabel;

  return {
    status,
    oauthTokenActive: jiraIntegration.oauthTokenActive ?? false,
    apiConnectivityActive: jiraIntegration.apiConnectivityActive ?? false,
    jiraInstallationId: jiraIntegration.jiraInstallationId ?? null,
    oauthInstallStepComplete: jiraIntegration.oauthInstallStepComplete ?? false,
    jiraAppInstalledComplete: jiraIntegration.jiraAppInstalledComplete ?? false,
    ...connectionHealth,
    connectionStatusLabel
  };
}

export function transformJiraConnectionHealth(connectionHealth) {
  if (!connectionHealth) {
    return null;
  }

  return {
    instanceUrl: connectionHealth.instanceUrl,
    connectionStatusLabel: connectionHealth.connectionStatusLabel,
    metrics: connectionHealth.metrics,
    systemChecks: connectionHealth.systemChecks,
    connectedSpaces: connectionHealth.connectedSpaces ?? [],
    addableSpaces: connectionHealth.addableSpaces ?? []
  };
}

export function isJiraConnectionHealthy({ status, jiraInstallationId, oauthTokenActive, apiConnectivityActive }) {
  return status === JIRA_INTEGRATION_STATUS.CONNECTED && Boolean(jiraInstallationId) && Boolean(oauthTokenActive) && Boolean(apiConnectivityActive);
}

/** Disconnected / auth expired / app removed — mutually exclusive priority for alerts. */
export function getJiraConnectionHealthIssue({ status, jiraInstallationId, oauthTokenActive, apiConnectivityActive }) {
  if (isJiraConnectionHealthy({ status, jiraInstallationId, oauthTokenActive, apiConnectivityActive })) {
    return null;
  }

  switch (status) {
    case JIRA_INTEGRATION_STATUS.NOT_CONNECTED:
    case JIRA_INTEGRATION_STATUS.PENDING:
      return JIRA_CONNECTION_HEALTH_ISSUE.DISCONNECTED;
    case JIRA_INTEGRATION_STATUS.AUTH_EXPIRED:
      return JIRA_CONNECTION_HEALTH_ISSUE.AUTH_EXPIRED;
    case JIRA_INTEGRATION_STATUS.INSTALL_FAILED:
      return JIRA_CONNECTION_HEALTH_ISSUE.APP_REMOVED;
    default:
      if (!oauthTokenActive) {
        return JIRA_CONNECTION_HEALTH_ISSUE.AUTH_EXPIRED;
      }

      if (!apiConnectivityActive) {
        return JIRA_CONNECTION_HEALTH_ISSUE.APP_REMOVED;
      }

      return JIRA_CONNECTION_HEALTH_ISSUE.DISCONNECTED;
  }
}

export function getJiraConnectionHealthCardState({ healthIssue, testConnectionHealth }) {
  if (testConnectionHealth?.success === false) {
    return {
      status: JIRA_SYSTEM_CHECK_STATUS.ERROR,
      subtitle: testConnectionHealth.error || JIRA_TEST_CONNECTION_LABELS.ERROR,
      linkLabel: null,
      linkAction: null
    };
  }

  switch (healthIssue) {
    case JIRA_CONNECTION_HEALTH_ISSUE.DISCONNECTED:
      return {
        status: JIRA_SYSTEM_CHECK_STATUS.ERROR,
        subtitle: JIRA_CONNECTION_HEALTH_LABELS.CONNECTION_HEALTH_SUBTITLE_DISCONNECTED,
        linkLabel: JIRA_CONNECTION_HEALTH_LABELS.CONNECTION_HEALTH_SUBTITLE_RECONNECT_RESUME,
        linkAction: JIRA_CONNECTION_HEALTH_LINK_ACTION.RECONNECT
      };
    case JIRA_CONNECTION_HEALTH_ISSUE.AUTH_EXPIRED:
      return {
        status: JIRA_SYSTEM_CHECK_STATUS.ERROR,
        subtitle: JIRA_CONNECTION_HEALTH_LABELS.CONNECTION_HEALTH_SUBTITLE_AUTH_REQUIRED,
        linkLabel: JIRA_CONNECTION_HEALTH_LABELS.CONNECTION_HEALTH_SUBTITLE_RECONNECT_JIRA,
        linkAction: JIRA_CONNECTION_HEALTH_LINK_ACTION.OPEN_AUTHORIZATION
      };
    case JIRA_CONNECTION_HEALTH_ISSUE.APP_REMOVED:
      return {
        status: JIRA_SYSTEM_CHECK_STATUS.ERROR,
        subtitle: JIRA_CONNECTION_HEALTH_LABELS.CONNECTION_HEALTH_SUBTITLE_APP_REMOVED,
        linkLabel: JIRA_CONNECTION_HEALTH_LABELS.CONNECTION_HEALTH_SUBTITLE_ATLASSIAN_MARKETPLACE,
        linkAction: JIRA_CONNECTION_HEALTH_LINK_ACTION.OPEN_MARKETPLACE
      };
    default:
      return {
        status: JIRA_SYSTEM_CHECK_STATUS.ACTIVE,
        subtitle: JIRA_CONNECTION_HEALTH_LABELS.CONNECTION_HEALTH_SUBTITLE,
        linkLabel: null,
        linkAction: null
      };
  }
}

export function getJiraConnectionLastUpdatedLabel(healthIssue) {
  switch (healthIssue) {
    case JIRA_CONNECTION_HEALTH_ISSUE.DISCONNECTED:
      return JIRA_CONNECTION_METRIC_LABELS.DISCONNECTED_ON;
    case JIRA_CONNECTION_HEALTH_ISSUE.AUTH_EXPIRED:
      return JIRA_CONNECTION_METRIC_LABELS.EXPIRED;
    case JIRA_CONNECTION_HEALTH_ISSUE.APP_REMOVED:
      return JIRA_CONNECTION_METRIC_LABELS.APP_REMOVED;
    default:
      return JIRA_CONNECTION_METRIC_LABELS.LAST_UPDATED;
  }
}
